import { GraphQLQuery, GraphQLResult } from "@aws-amplify/api";
import {
  InfiniteData,
  QueryKey,
  UseMutationResult,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import React, { useCallback, useEffect } from "react";
import { Button, Collapse } from "react-daisyui";
import {
  execQuery,
  mutations,
  subscribe,
  subscriptions,
  types,
  wrapMutation,
} from "../graphql";
import { getBlockInteractionPaging } from "../graphql/BlockInteractionPaging";
import BlockComponent from "../components/blocks";

interface BlockProps {
  zone: types.Zone;
  currentUser: types.User | null;
  block: types.Block;
  participants: types.Participant[];
  query: ReturnType<typeof useInfiniteQuery<types.Block>>;
  addInteractionMutation: UseMutationResult<
    GraphQLResult<GraphQLQuery<types.AddInteractionMutation>>,
    any,
    types.AddInteractionMutationVariables,
    {
      previousState: InfiniteData<types.Block>;
      cacheKey: string[];
    }
  >;
}

export default function Block(
  props: Omit<BlockProps, "query" | "addInteractionMutation">,
) {
  const { zone, block } = props;
  const [isOpen, setIsOpen] = React.useState(true);
  const queryClient = useQueryClient();

  const mergeInteraction = useCallback(
    async (
      newInteraction: types.AddInteractionMutationVariables | types.Interaction,
    ) => {
      const cacheKey = ["zone", zone.zoneId, "blocks", block.blockId];
      await queryClient.cancelQueries(cacheKey);
      const previousState =
        queryClient.getQueryData<InfiniteData<types.Block>>(cacheKey)!;
      if (previousState) {
        let foundInteraction = false;
        const newState = {
          pages: previousState.pages.map((page) => ({
            ...page,
            interactions: {
              ...page.interactions,
              items: page.interactions?.items.map((interaction) => {
                if (
                  interaction.interactionKey === newInteraction.interactionKey
                ) {
                  foundInteraction = true;
                  return newInteraction;
                } else {
                  return interaction;
                }
              }),
            },
          })),
          pageParams: [...previousState.pageParams],
        };
        if (!foundInteraction) {
          newState.pages[0].interactions?.items?.unshift(newInteraction);
        }
        queryClient.setQueryData(cacheKey, newState);
      }
      return { previousState, cacheKey };
    },
    [zone.zoneId, block.blockId, queryClient],
  );
  const addInteractionMutation = useMutation(
    wrapMutation(mutations.addInteraction),
    {
      onMutate: mergeInteraction,
      onError: (
        err: any,
        newInteraction: types.AddInteractionMutationVariables,
        context?: {
          previousState: InfiniteData<types.Block>;
          cacheKey: QueryKey;
        },
      ): void => {
        if (context?.previousState) {
          queryClient.setQueryData(context.cacheKey, context.previousState);
        }
      },
    },
  );

  const query = useInfiniteQuery(
    ["zone", zone.zoneId, "blocks", block.blockId],
    {
      queryFn: async ({ pageParam: nextToken }) =>
        execQuery(getBlockInteractionPaging, {
          zoneId: zone.zoneId,
          blockId: block.blockId,
          limit: 300,
          nextToken,
        }).then((res) => res.data?.getBlock!),
      getNextPageParam: (lastPage) => lastPage?.interactions?.nextToken,
      staleTime: Infinity,
    },
  );
  useEffect(() => {
    const subscription = subscribe(subscriptions.onInteraction, {
      blockId: block.blockId,
    }).subscribe({
      next: ({ value }) => {
        const { data } = value;
        console.log(`Received interaction for block ${block.blockId}`, data);
        if (data?.onInteraction) {
          mergeInteraction(data.onInteraction);
        }
      },
    });
    console.log("Subscribed");
    return () => {
      console.log("Unsubscribed");
      subscription.unsubscribe();
    };
  }, [zone.zoneId, block.blockId, mergeInteraction]);

  return (
    <Collapse
      id={block.blockId}
      checkbox
      icon="arrow"
      open={isOpen}
      className="rounded-md"
      onToggle={() => setIsOpen((isOpen) => !isOpen)}
    >
      <Collapse.Title className="bg-base-300">
        {block.blockType} <Button className="btn btn-secondary">Edit</Button>
      </Collapse.Title>
      <Collapse.Content className="bg-base-100 pt-4">
        {BlockComponent(block.blockType)({
          ...{ ...props, query, addInteractionMutation },
        })}
      </Collapse.Content>
    </Collapse>
  );
}

export type { BlockProps };
